'use client';

import { useEffect, useState } from 'react';
import type {
    EpisodeComment, EpisodeCommentGroup, EpisodeCommentsContext, EpisodeCommentsDocument,
} from '@/_contract/episodeComments';
import { DeveloperDetails } from './DeveloperJson';
import { parseEpisodeComments } from '@/_pure/episodeComments';
import { cosmosSpaceUrl } from '@/_pure/cosmosRoute';

const groupKey = (group: EpisodeCommentGroup) => JSON.stringify([group.sceneId, group.beatId]);
const isLocalDevelopment = process.env.NODE_ENV === 'development';

type CommentState = {
    key: string;
    document?: EpisodeCommentsDocument;
    drafts: Record<string, readonly EpisodeComment[]>;
    loading?: boolean;
    saving?: boolean;
    error?: string;
    message?: string;
};

async function responseDocument(response: Response): Promise<EpisodeCommentsDocument> {
    const result: unknown = await response.json();
    if (!result || typeof result !== 'object' || Array.isArray(result)) throw new Error('Invalid comments response.');
    const payload = result as Record<string, unknown>;
    if (!response.ok) throw new Error(typeof payload.error === 'string' ? payload.error : `Comment request failed (${response.status}).`);
    if (typeof payload.version !== 'string' || !Array.isArray(payload.groups)) throw new Error('Invalid comments document.');
    const groups: EpisodeCommentGroup[] = payload.groups.map((value: unknown) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid comment group.');
        const group = value as Record<string, unknown>;
        if (typeof group.label !== 'string' || !Array.isArray(group.comments)
            || (group.sceneId !== undefined && typeof group.sceneId !== 'string')
            || (group.beatId !== undefined && (typeof group.beatId !== 'string' || !group.sceneId))) {
            throw new Error('Invalid comment group identity.');
        }
        return { label: group.label, sceneId: group.sceneId as string | undefined, beatId: group.beatId as string | undefined,
            comments: parseEpisodeComments(group.comments).comments! };
    });
    return { version: payload.version, groups };
}

export function useEpisodeComments(context: EpisodeCommentsContext | undefined, spaceSlug: string, enabled: boolean) {
    const key = `${spaceSlug}:${context?.handle ?? ''}`;
    const handle = context?.handle;
    const [state, setState] = useState<CommentState>({ key, drafts: {} });
    const [reload, setReload] = useState(0);
    const [showInScene, setShowInScene] = useState(false);
    useEffect(() => {
        if (!enabled || !handle || !isLocalDevelopment) return;
        const abort = new AbortController();
        setState({ key, drafts: {}, loading: true });
        void fetch(`/api/episode-comments?${new URLSearchParams({ spaceSlug, handle })}`,
            { signal: abort.signal, cache: 'no-store' })
            .then(responseDocument)
            .then(document => {
                if (!abort.signal.aborted) setState({ key, document, drafts: {} });
            })
            .catch(error => {
                if (abort.signal.aborted) return;
                console.error('[episode-comments] Load failed:', error);
                setState({ key, drafts: {}, error: error instanceof Error ? error.message : String(error) });
            });
        return () => abort.abort();
    }, [enabled, key, spaceSlug, handle, reload]);
    useEffect(() => {
        if (!enabled || state.key !== key || !Object.keys(state.drafts).length) return;
        const beforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
    }, [enabled, key, state.key, state.drafts]);
    const current = state.key === key ? state : { key, drafts: {} };
    const groups = context?.groups.map(group => {
        const saved = current.document?.groups.find(entry => groupKey(entry) === groupKey(group));
        return { ...group, comments: saved?.comments ?? group.comments };
    }) ?? [];
    const editable = enabled && isLocalDevelopment && !!current.document && !current.loading;
    const draft = (group: EpisodeCommentGroup, comments: readonly EpisodeComment[]) =>
        setState(previous => previous.key !== key ? previous : {
            ...previous, drafts: { ...previous.drafts, [groupKey(group)]: comments }, message: undefined,
        });
    const cancel = (group: EpisodeCommentGroup) => setState(previous => {
        if (previous.key !== key) return previous;
        const drafts = { ...previous.drafts };
        delete drafts[groupKey(group)];
        return { ...previous, drafts, error: undefined };
    });
    const save = async (group: EpisodeCommentGroup) => {
        const comments = current.drafts[groupKey(group)];
        if (!editable || current.saving || !comments || !current.document || !context) return;
        setState(previous => ({ ...previous, saving: true, error: undefined, message: undefined }));
        try {
            const document = await responseDocument(await fetch('/api/episode-comments', {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ spaceSlug, handle: context.handle, version: current.document.version,
                    sceneId: group.sceneId, beatId: group.beatId, comments }),
            }));
            setState(previous => {
                if (previous.key !== key) return previous;
                const drafts = { ...previous.drafts };
                delete drafts[groupKey(group)];
                return { ...previous, document, drafts, saving: false, message: 'Comments saved to episode JSON.' };
            });
        } catch (error) {
            console.error('[episode-comments] Save failed:', error);
            setState(previous => previous.key !== key ? previous : { ...previous, saving: false,
                error: error instanceof Error ? error.message : String(error) });
        }
    };
    return { groups, drafts: current.drafts, editable, loading: current.loading, saving: current.saving,
        error: current.error, message: current.message, showInScene, setShowInScene, draft, cancel, save,
        reload: () => {
            if (Object.keys(current.drafts).length && !window.confirm('Discard unsaved comment edits and reload from disk?')) return;
            setReload(value => value + 1);
        },
        dirtyCount: Object.keys(current.drafts).length,
        signInUrl: `/api/auth/signin?callbackUrl=${encodeURIComponent(cosmosSpaceUrl(spaceSlug, handle))}` };
}

type CommentsOwner = ReturnType<typeof useEpisodeComments>;
const buttonStyle = { margin: '4px 5px 4px 0', padding: '4px 8px', background: 'rgba(255,255,255,0.12)',
    color: 'inherit', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 5 };

function CommentsList({ group }: { group: EpisodeCommentGroup }) {
    return group.comments.length ? <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
        {group.comments.map(comment => <li key={comment.id}
            style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', marginBottom: 6 }}>{comment.text}</li>)}
    </ul> : <p style={{ opacity: 0.65 }}>No comments.</p>;
}

export function EpisodeCommentsPanel({ owner }: { owner: CommentsOwner }) {
    return <DeveloperDetails title="Comments">
        <label style={{ display: 'block', marginTop: 8 }}>
            <input type="checkbox" checked={owner.showInScene}
                onChange={event => owner.setShowInScene(event.target.checked)} /> Show comments in scene
        </label>
        {isLocalDevelopment ? <>
            <button type="button" style={buttonStyle} disabled={owner.saving || owner.loading} onClick={owner.reload}>Reload comments</button>
            <p>Edits save to authored JSON locally; hosted comments are read-only.</p>
        </> : <p>Read-only comments from the authored episode.</p>}
        {owner.loading ? <p role="status">Loading source comments...</p> : null}
        {owner.error ? <p role="alert">{owner.error}
            {owner.error === 'Unauthorized' ? <> — <a href={owner.signInUrl}>Sign in to edit comments</a></> : null}
        </p> : null}
        {owner.message ? <p role="status">{owner.message}</p> : null}
        {owner.dirtyCount ? <p role="status">{owner.dirtyCount} segment(s) with unsaved edits. Save each segment before leaving this episode.</p> : null}
        {owner.groups.map(group => {
            const draft = owner.drafts[groupKey(group)];
            return <section key={groupKey(group)} aria-label={`${group.label} comments`}
                style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 8, marginTop: 8 }}>
                <strong>{group.label}</strong>
                {draft ? <>
                    {draft.map((comment, index) => <div key={comment.id} style={{ marginTop: 8 }}>
                        <textarea aria-label={`${group.label} comment ${index + 1}`} value={comment.text} maxLength={10000}
                            disabled={owner.saving} rows={3}
                            onChange={event => owner.draft(group, draft.map(entry =>
                                entry.id === comment.id ? { ...entry, text: event.target.value } : entry))}
                            style={{ width: '100%', boxSizing: 'border-box', color: 'inherit', background: 'rgba(255,255,255,0.08)' }} />
                        <button type="button" style={buttonStyle} disabled={owner.saving}
                            onClick={() => owner.draft(group, draft.filter(entry => entry.id !== comment.id))}>Delete comment {index + 1}</button>
                    </div>)}
                    <button type="button" style={buttonStyle} disabled={owner.saving || draft.length >= 100}
                        onClick={() => owner.draft(group, [...draft, { id: `comment-${crypto.randomUUID()}`, text: '' }])}>Add comment</button>
                    <button type="button" style={buttonStyle}
                        disabled={owner.saving || draft.some(comment => !comment.text.trim())}
                        onClick={() => void owner.save(group)}>Save comments</button>
                    <button type="button" style={buttonStyle} disabled={owner.saving} onClick={() => owner.cancel(group)}>Cancel edits</button>
                </> : <>
                    <CommentsList group={group} />
                    {owner.editable ? <button type="button" style={buttonStyle} disabled={owner.saving}
                        onClick={() => owner.draft(group, group.comments.map(comment => ({ ...comment })))}>Edit comments</button> : null}
                </>}
            </section>;
        })}
    </DeveloperDetails>;
}

export function EpisodeCommentsScene({ owner }: { owner: CommentsOwner }) {
    if (!owner.showInScene) return null;
    return <aside aria-label="Episode comments" style={{ position: 'absolute', top: 12, right: 12,
        width: 'min(340px, calc(100% - 24px))', maxHeight: 'calc(100% - 180px)', overflowY: 'auto',
        zIndex: 11, pointerEvents: 'auto', padding: 12, boxSizing: 'border-box', borderRadius: 10,
        background: 'rgba(0,0,0,0.82)', color: '#fff', fontSize: 13 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>Comments</strong>
            <button type="button" style={buttonStyle} onClick={() => owner.setShowInScene(false)}>Hide comments</button>
        </div>
        {owner.groups.map(group => <section key={groupKey(group)} style={{ marginTop: 10 }}>
            <strong>{group.label}</strong><CommentsList group={group} />
        </section>)}
    </aside>;
}
